import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Button, Table, message, Popconfirm, Space, Tag, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import ObservatoryService from '../services/ObservatoryService';

const { Option } = Select;

/**
 * AlertRulesModal - Modal for creating and managing alert rules
 */
const AlertRulesModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    agentNames: [],
    agentTypes: [],
    executionContexts: []
  });

  // Available metrics from observability_metrics table
  const metricOptions = [
    { value: 'avg_llm_latency_ms', label: 'LLM Latency', unit: 'milliseconds' },
    { value: 'avg_trace_latency_ms', label: 'Trace Latency', unit: 'milliseconds' },
    { value: 'total_cost', label: 'Total Cost', unit: 'dollars' },
    { value: 'input_cost_per_trace', label: 'Input Cost Per Trace', unit: 'dollars' },
    { value: 'output_cost_per_trace', label: 'Output Cost Per Trace', unit: 'dollars' },
    { value: 'total_input_tokens', label: 'Total Input Tokens', unit: 'count' },
    { value: 'total_output_tokens', label: 'Total Output Tokens', unit: 'count' },
    { value: 'avg_tokens_per_trace', label: 'Avg Tokens Per Trace', unit: 'count' },
    { value: 'trace_count', label: 'Trace Count', unit: 'count' },
    { value: 'llm_call_count', label: 'LLM Call Count', unit: 'count' },
    { value: 'success_rate', label: 'Success Rate', unit: 'percent' },
    { value: 'failed_traces', label: 'Failed Traces', unit: 'count' }
  ];

  const comparisonOptions = [
    { value: 'greater_than', label: 'Greater Than (>)' },
    { value: 'less_than', label: 'Less Than (<)' },
    { value: 'greater_or_equal', label: 'Greater or Equal (>=)' },
    { value: 'less_or_equal', label: 'Less or Equal (<=)' },
    { value: 'equal_to', label: 'Equal To (=)' }
  ];

  const severityOptions = [
    { value: 'info', label: 'Info', color: 'blue' },
    { value: 'warning', label: 'Warning', color: 'orange' },
    { value: 'critical', label: 'Critical', color: 'red' }
  ];

  const unitOptions = [
    { value: 'seconds', label: 'Seconds' },
    { value: 'milliseconds', label: 'Milliseconds' },
    { value: 'dollars', label: 'Dollars' },
    { value: 'count', label: 'Count' },
    { value: 'percent', label: 'Percent' }
  ];

  useEffect(() => {
    if (visible) {
      loadAlertRules();
      loadFilterOptions();
    }
  }, [visible]);

  const loadAlertRules = async () => {
    try {
      setLoading(true);
      const data = await ObservatoryService.getAlertRules();
      setRules(data.rules || []);
    } catch (error) {
      console.error('Error loading alert rules:', error);
      message.error(`Failed to load alert rules: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const data = await ObservatoryService.getFilterOptions('30d');
      setFilterOptions({
        agentNames: data.agent_names || [],
        agentTypes: data.agent_types || [],
        executionContexts: data.execution_contexts || []
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Convert threshold to appropriate unit if needed
      let thresholdValue = values.threshold_value;
      if (values.threshold_unit === 'seconds' && 
          (values.metric_name === 'avg_llm_latency_ms' || values.metric_name === 'avg_trace_latency_ms')) {
        thresholdValue = thresholdValue * 1000; // Convert seconds to milliseconds
      }

      const ruleData = {
        rule_name: values.rule_name,
        enabled: values.enabled !== undefined ? values.enabled : true,
        agent_name: values.agent_name === '*' ? null : values.agent_name,
        agent_type: values.agent_type === '*' ? null : values.agent_type,
        execution_context: values.execution_context === '*' ? null : values.execution_context,
        metric_name: values.metric_name,
        comparison_operator: values.comparison_operator,
        threshold_value: thresholdValue,
        threshold_unit: values.threshold_unit,
        evaluation_period: values.evaluation_period || 'hourly',
        lookback_periods: values.lookback_periods || 1,
        alert_severity: values.alert_severity || 'warning'
      };

      if (editingRule) {
        await ObservatoryService.updateAlertRule(editingRule.id, ruleData);
        message.success('Alert rule updated successfully');
      } else {
        await ObservatoryService.createAlertRule(ruleData);
        message.success('Alert rule created successfully');
      }

      form.resetFields();
      setEditingRule(null);
      loadAlertRules();
    } catch (error) {
      console.error('Error saving alert rule:', error);
      message.error(`Failed to save alert rule: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    
    // Convert milliseconds to seconds for display if needed
    let displayValue = rule.threshold_value;
    if (rule.threshold_unit === 'seconds' && 
        (rule.metric_name === 'avg_llm_latency_ms' || rule.metric_name === 'avg_trace_latency_ms')) {
      displayValue = displayValue / 1000;
    }

    form.setFieldsValue({
      rule_name: rule.rule_name,
      enabled: rule.enabled,
      agent_name: rule.agent_name || '*',
      agent_type: rule.agent_type || '*',
      execution_context: rule.execution_context || '*',
      metric_name: rule.metric_name,
      comparison_operator: rule.comparison_operator,
      threshold_value: displayValue,
      threshold_unit: rule.threshold_unit,
      evaluation_period: rule.evaluation_period,
      lookback_periods: rule.lookback_periods,
      alert_severity: rule.alert_severity
    });
  };

  const handleDelete = async (ruleId) => {
    try {
      await ObservatoryService.deleteAlertRule(ruleId);
      message.success('Alert rule deleted successfully');
      loadAlertRules();
    } catch (error) {
      console.error('Error deleting alert rule:', error);
      message.error(`Failed to delete alert rule: ${error.message}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingRule(null);
  };

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 180,
      render: (text) => text || <span style={{ color: '#999' }}>* (All)</span>
    },
    {
      title: 'Agent Type',
      dataIndex: 'agent_type',
      key: 'agent_type',
      width: 130,
      render: (text) => text || <span style={{ color: '#999' }}>*</span>
    },
    {
      title: 'Execution Type',
      dataIndex: 'execution_context',
      key: 'execution_context',
      width: 130,
      render: (text) => text || <span style={{ color: '#999' }}>*</span>
    },
    {
      title: 'Metric',
      dataIndex: 'metric_name',
      key: 'metric_name',
      width: 150,
      render: (text) => {
        const metric = metricOptions.find(m => m.value === text);
        return metric ? metric.label : text;
      }
    },
    {
      title: 'Condition',
      key: 'condition',
      width: 200,
      render: (_, record) => {
        const operator = comparisonOptions.find(o => o.value === record.comparison_operator);
        const displayValue = record.threshold_unit === 'seconds' && 
          (record.metric_name === 'avg_llm_latency_ms' || record.metric_name === 'avg_trace_latency_ms')
          ? (record.threshold_value / 1000).toFixed(0)
          : record.threshold_value;
        return `${operator?.label || record.comparison_operator} ${displayValue}`;
      }
    },
    {
      title: 'Unit',
      dataIndex: 'threshold_unit',
      key: 'threshold_unit',
      width: 100,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Severity',
      dataIndex: 'alert_severity',
      key: 'alert_severity',
      width: 100,
      render: (severity) => {
        const sev = severityOptions.find(s => s.value === severity);
        return <Tag color={sev?.color}>{severity?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Modal
      title="Alert Rules Management"
      visible={visible}
      onCancel={onClose}
      width="95%"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
    >
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>
          {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
        </h3>
        
        <Form
          form={form}
          layout="inline"
          onFinish={handleSubmit}
          style={{ marginBottom: '16px' }}
        >
          <Form.Item
            name="rule_name"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '200px' }}
          >
            <Input placeholder="Rule Name" />
          </Form.Item>

          <Form.Item
            name="agent_name"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '180px' }}
          >
            <Select placeholder="Agent Name">
              <Option value="*">* (All Agents)</Option>
              {filterOptions.agentNames.map(name => (
                <Option key={name} value={name}>{name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="agent_type"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '150px' }}
          >
            <Select placeholder="Agent Type">
              <Option value="*">* (All)</Option>
              {filterOptions.agentTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="execution_context"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '150px' }}
          >
            <Select placeholder="Execution Mode">
              <Option value="*">* (All)</Option>
              {filterOptions.executionContexts.map(ctx => (
                <Option key={ctx} value={ctx}>{ctx}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="metric_name"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '180px' }}
          >
            <Select placeholder="Metric">
              {metricOptions.map(metric => (
                <Option key={metric.value} value={metric.value}>
                  {metric.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="comparison_operator"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '150px' }}
          >
            <Select placeholder="Comparison">
              {comparisonOptions.map(op => (
                <Option key={op.value} value={op.value}>{op.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="threshold_value"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '120px' }}
          >
            <InputNumber
              placeholder="Threshold"
              style={{ width: '100%' }}
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="threshold_unit"
            rules={[{ required: true, message: 'Required' }]}
            style={{ width: '130px' }}
          >
            <Select placeholder="Unit">
              {unitOptions.map(unit => (
                <Option key={unit.value} value={unit.value}>{unit.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="alert_severity"
            initialValue="warning"
            style={{ width: '120px' }}
          >
            <Select placeholder="Severity">
              {severityOptions.map(sev => (
                <Option key={sev.value} value={sev.value}>
                  <Tag color={sev.color}>{sev.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="enabled"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={submitting}
                style={{ background: '#92A372' }}
              >
                {editingRule ? 'Update' : 'Add'}
              </Button>
              {editingRule && (
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </div>

      <div>
        <h3 style={{ marginBottom: '16px' }}>Current Alert Rules</h3>
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1400 }}
          size="small"
        />
      </div>
    </Modal>
  );
};

export default AlertRulesModal;